import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, Settings } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import ClientFormDialog from '../components/ClientFormDialog';
import ClinicToday from '../components/ClinicToday';
import ClinicSnapshotPanel from '../components/clinic/ClinicSnapshotPanel';
import ClinicConfigPanel from '../components/clinic/ClinicConfigPanel';
import AvailabilityGrid from '../components/clinic/AvailabilityGrid';
import OutsideLocationsPanel from '../components/clinic/OutsideLocationsPanel';
import ClinicBonusPanel from '../components/clinic/ClinicBonusPanel';
import ClinicMonthlyTrendsStrip from '../components/clinic/ClinicMonthlyTrendsStrip';
import ClinicUtilizationPanel from '../components/clinic/ClinicUtilizationPanel';
import EvalToSessionChart from '../components/EvalToSessionChart';
import { CohortConversionTable, UnderTargetCallout } from '../components/MonthlyTablesSection';
import DuplicateWarningDialog from '../components/DuplicateWarningDialog';

const EVAL_ROLES = new Set(['Office Director/SLP', 'SLP', 'CF-SLP', 'CF', 'PRN']);

export default function ClinicView() {
  const { clinicId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clinic, setClinic] = useState(null);
  const [kpi, setKpi] = useState(null);
  const [setup, setSetup] = useState({});
  const [clinicians, setClinicians] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newLoc, setNewLoc] = useState({ name: '', address: '', type: 'ABA Center', accepting_status: 'Yes', notes: '' });
  const [notesDraft, setNotesDraft] = useState('');
  const [evalToSession, setEvalToSession] = useState(null);
  // Cohort conversion rows for THIS clinic only (iter 108). Same endpoint
  // feeds the Dashboard and every clinic view — we just slice the
  // `cohorts_by_clinic` map by clinic name.
  const [cohortRows, setCohortRows] = useState(null);
  // Duplicate-warning modal wiring for POST /clients probable_duplicate 409
  const [dupOpen, setDupOpen] = useState(false);
  const [dupMatches, setDupMatches] = useState([]);
  const [dupPayload, setDupPayload] = useState(null);

  // Setup & Availability drawer (iter 278) — starts OPEN for ODs (they
  // actively edit their clinic's config) and CLOSED for everyone else so
  // the day-first content (Pipeline, Clinic Today, analytics) sits near
  // the top of the page. Persists per-clinic in localStorage so users
  // can override the default.
  const setupStorageKey = clinicId ? `clinic-setup-open:${clinicId}` : null;
  const [setupOpen, setSetupOpen] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      const saved = setupStorageKey ? window.localStorage.getItem(setupStorageKey) : null;
      if (saved === '1') return true;
      if (saved === '0') return false;
    } catch {}
    return user?.role === 'office_director';
  });
  useEffect(() => {
    if (!setupStorageKey || typeof window === 'undefined') return;
    try { window.localStorage.setItem(setupStorageKey, setupOpen ? '1' : '0'); } catch {}
  }, [setupOpen, setupStorageKey]);

  // Derived clinician lists — memoised so config-panel re-renders don't recompute
  const { bilingualClinicians, clinicClinicianNames } = useMemo(() => {
    if (!clinic) return { bilingualClinicians: [], clinicClinicianNames: [] };
    const clinicClinicians = clinicians.filter(c => Array.isArray(c.clinics) && c.clinics.includes(clinic.name));
    const evalEligible = clinicClinicians.filter(c => EVAL_ROLES.has(c.role || ''));
    return {
      bilingualClinicians: evalEligible.filter(c => c.bilingual === 'Yes'),
      clinicClinicianNames: evalEligible.map(c => c.name),
    };
  }, [clinic, clinicians]);

  const canEdit = useMemo(() => {
    if (!clinic || !user) return false;
    if (['super_user', 'admin', 'hr_payroll'].includes(user.role)) return true;
    const list = user.clinics?.length ? user.clinics : (user.clinic ? [user.clinic] : []);
    return user.role === 'office_director' && list.includes(clinic.name);
  }, [clinic, user]);

  // iter 70 — user directive: every non-pending role has full R/W to the
  // client modal ("all client related info") regardless of clinic scope.
  // The clinic-level `canEdit` above still gates availability / config
  // writes.
  const canEditClient = useMemo(() => {
    if (!user) return false;
    return ['super_user', 'admin', 'hr_payroll', 'office_director', 'read_only'].includes(user.role);
  }, [user]);

  const load = useCallback(async () => {
    try {
      // Scope guard (2026-02 RBAC audit): fetch the clinic doc — the
      // backend now returns 403 for an OD trying to open another
      // clinic. We also do a frontend redirect on 403/404 further
      // down so the UI doesn't show a half-loaded page. The earlier
      // `list.length > 0` bypass was removed — if an OD has no clinic
      // scope at all (roster misconfig) the backend fail-closes to 403.
      const r = await api.get(`/clinics/${clinicId}`);
      setClinic(r.data);
      setNotesDraft(r.data.notes || '');
      const [kr, sr, cr] = await Promise.all([
        api.get(`/clinics/${clinicId}/kpis`),
        api.get('/setup'),
        api.get('/clinicians'),
      ]);
      setKpi(kr.data);
      setSetup(sr.data || {});
      setClinicians(cr.data || []);
      // ── Eval → Session chart data (iter 90) ────────────────────────
      // We ask the org-wide endpoint and slice into this clinic's series
      // client-side — one request feeds any clinic tab. Failure is silent
      // (chart just doesn't render) so it never blocks the page.
      try {
        const mr = await api.get('/dashboard/monthly-tables', {
          params: { months: 12, scope: 'group' },
        });
        setEvalToSession(mr.data?.eval_to_therapy_by_clinic?.[r.data.name] || []);
        setCohortRows(mr.data?.cohorts_by_clinic?.[r.data.name] || []);
      } catch { setEvalToSession([]); setCohortRows([]); }
    } catch (e) {
      // 403 from the backend means this user cannot see this clinic — redirect silently.
      if (e?.response?.status === 403 || e?.response?.status === 404) {
        toast.error("You don't have access to that clinic. Redirected to Dashboard.");
        navigate('/dashboard', { replace: true });
      } else {
        toast.error(e?.response?.data?.detail || 'Failed to load clinic');
      }
    }
  }, [clinicId, user, navigate]);

  useEffect(() => { load(); }, [load]);

  if (!clinic || !kpi) return <div className="text-[#685B72]" data-testid="clinic-loading">Loading clinic…</div>;

  const handleSaveClient = async (payload) => {
    // If this is an EXISTING client, we PATCH — the duplicate check is
    // strictly at create time. Otherwise POST, handling the 409 probable-
    // duplicate response by opening a review dialog on the caller side.
    if (selectedClient?.client_id) {
      try {
        const r = await api.patch(`/clients/${selectedClient.client_id}`, payload);
        setSelectedClient(r.data);
        setDialogOpen(false);
        toast.success('Client updated');
        load();
      } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
      return;
    }
    try {
      const r = await api.post(`/clients`, { ...payload, clinic: payload.clinic || clinic.name });
      setSelectedClient(r.data);
      setDialogOpen(false);
      toast.success('Client created');
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.error === 'probable_duplicate') {
        setDupPayload(payload);
        setDupMatches(detail.matches || []);
        setDupOpen(true);
        return;
      }
      toast.error(typeof detail === 'string' ? detail : 'Create failed');
    }
  };
  const forceCreate = async () => {
    try {
      const r = await api.post(`/clients`, { ...dupPayload, clinic: dupPayload.clinic || clinic.name, force: true });
      setDupOpen(false);
      setDialogOpen(false);
      setSelectedClient(r.data);
      toast.success('Client created (duplicate override)');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Create failed'); }
  };

  const handleDeleteClient = async (c) => {
    try {
      await api.delete(`/clients/${c.client_id}`);
      setDialogOpen(false);
      toast.success(`${c.client_name || 'Client'} deleted`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const updateCell = async (time, day, value) => {
    try {
      await api.patch(`/clinics/${clinicId}/availability`, { time, day, value });
      setClinic(c => ({
        ...c,
        availability_grid: { ...c.availability_grid, [time]: { ...(c.availability_grid?.[time] || {}), [day]: value } },
      }));
    } catch { toast.error('Failed to update slot'); }
  };

  const patchClinic = async (patch) => {
    try {
      await api.patch(`/clinics/${clinicId}`, patch);
      setClinic(c => ({ ...c, ...patch }));
      toast.success('Saved');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  const addLocation = async () => {
    if (!newLoc.name.trim()) return toast.error('Location name required');
    try {
      await api.post(`/clinics/${clinicId}/locations`, newLoc);
      setNewLoc({ name: '', address: '', type: 'ABA Center', accepting_status: 'Yes', notes: '' });
      toast.success('Location added');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Add failed'); }
  };

  const updateLocation = async (idx, patch) => {
    try {
      await api.patch(`/clinics/${clinicId}/locations/${idx}`, patch);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  const removeLocation = async (idx) => {
    if (!window.confirm('Remove this location?')) return;
    await api.delete(`/clinics/${clinicId}/locations/${idx}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="clinic-page">
      <ClinicSnapshotPanel clinic={clinic} kpi={kpi} />

      {/* Setup & Availability — collapsible group (iter 278). Combines
          Clinic configuration, Availability grid, and Outside locations
          into one section so that day-first content (analytics, today,
          bonus) stays near the top of the page. Default open for ODs,
          closed for everyone else. */}
      <Card
        className="rounded-2xl border-[#E8E5EC] bg-white overflow-hidden"
        data-testid="clinic-setup-drawer"
      >
        <button
          type="button"
          onClick={() => setSetupOpen(v => !v)}
          className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-[#FBF8F4] transition-colors"
          aria-expanded={setupOpen}
          data-testid="clinic-setup-toggle"
        >
          <div className="flex items-center gap-3">
            <div className="rounded-full p-2" style={{ background: '#685B72', color: '#fff' }}>
              <Settings size={16} strokeWidth={2.4} />
            </div>
            <div>
              <h3 className="font-serif text-xl text-[#2D2833] leading-none">Setup & Availability</h3>
              <div className="text-[11px] text-[#685B72] mt-1">
                Clinic configuration · scheduling rules · availability grid · outside locations
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-[#685B72]">
            <span className="text-[11px] uppercase tracking-widest font-semibold">
              {setupOpen ? 'Hide' : 'Show'}
            </span>
            {setupOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </div>
        </button>
        {setupOpen && (
          <div className="px-6 pb-6 space-y-6 border-t border-[#E8E5EC]" data-testid="clinic-setup-body">
            <div className="pt-6">
              <ClinicConfigPanel
                clinic={clinic} setClinic={setClinic}
                canEdit={canEdit} patchClinic={patchClinic}
                setup={setup} setSetup={setSetup}
                bilingualClinicians={bilingualClinicians}
                clinicClinicianNames={clinicClinicianNames}
                notesDraft={notesDraft} setNotesDraft={setNotesDraft}
              />
            </div>
            <AvailabilityGrid clinic={clinic} canEdit={canEdit} updateCell={updateCell} />
            <OutsideLocationsPanel
              clinic={clinic} canEdit={canEdit}
              newLoc={newLoc} setNewLoc={setNewLoc}
              addLocation={addLocation}
              updateLocation={updateLocation}
              removeLocation={removeLocation}
            />
          </div>
        )}
      </Card>

      <div data-testid="clinic-today"><ClinicToday clinicName={clinic.name} /></div>

      {/* Salaried under-target · 4 weeks in a row — scoped to this clinic
          (iter 219). Sits directly above the trends strip so ODs get the
          shortfall context before the visuals. */}
      <UnderTargetCallout clinic={clinic.name} />

      {/* Monthly trends (iter 278) — Sessions-only full-width chart. Utilization
          and Eval→Therapy strips were removed since the Utilization panel
          below and the Eval→Session chart already cover those views. */}
      <ClinicMonthlyTrendsStrip clinicName={clinic.name} />

      {/* Eval → Session Conversion (iter 90 — clinician request) */}
      {evalToSession !== null && (
        <EvalToSessionChart
          data={evalToSession}
          defaultLabel={clinic.name}
          testId={`clinic-eval-to-session-${clinic.name.replace(/\W+/g, '_')}`}
        />
      )}

      <ClinicUtilizationPanel clinicId={clinicId} clinicName={clinic.name} />

      {/* Bonus tracker (iter 278 — moved to the bottom of the tab at user
          request; sits below utilization detail). */}
      <ClinicBonusPanel clinicId={clinicId} clinicName={clinic.name} />

      <ClientFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        client={selectedClient}
        setup={setup}
        clinicLocations={clinic.outside_locations || []}
        onSave={handleSaveClient}
        onDelete={handleDeleteClient}
        onReactivated={load}
        readOnly={!canEditClient}
      />

      <DuplicateWarningDialog
        open={dupOpen}
        onOpenChange={setDupOpen}
        matches={dupMatches}
        onCancel={() => setDupOpen(false)}
        onCreateAnyway={forceCreate}
      />
    </div>
  );
}
